import { useMemo, useState } from 'react'
import {
  Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts'
import type { Series, TimeRange } from '../api/types'
import { Card } from './Card'
import {
  RESOLUTION_LABEL, SERIES_SLOTS, formatValue, seriesColor, seriesCycleColor,
  seriesDash, timeTickFormatter,
} from '../theme/charts'

type Props = {
  title: string
  series: Series[]
  range: TimeRange
  /** 'line' for independent series, 'stacked-area' for parts of a whole. */
  mode?: 'line' | 'area' | 'stacked-area'
  height?: number
  unit?: string
  /** Stable key for remembering the collapsed state per user. */
  prefKey?: string
  /**
   * Fold the tail past eight series into one "Other" band. On by default.
   *
   * <p>Set false where every series has to stay named — the import providers,
   * where the whole question is which provider is misbehaving and folding would
   * hide it. Hues then cycle with a per-lap dash pattern instead.
   */
  foldSeries?: boolean
}

interface Row {
  t: string
  [seriesName: string]: string | number | null
}

/**
 * Time-series chart.
 *
 * <p>One y-axis, always. Two measures on different scales get two charts —
 * a second axis makes the crossing point of the two lines look meaningful when
 * it is an artefact of the scaling.
 *
 * <p>Every series carries a legend entry with its current value, so identity
 * never rests on colour alone. That is a requirement rather than a nicety here:
 * three of the light-mode series colours fall below 3:1 against the surface.
 */
export function MetricChart({
  title, series, range, mode = 'line', height = 220, unit, prefKey,
  foldSeries = true,
}: Props) {
  const [showTable, setShowTable] = useState(false)

  const effectiveUnit = unit ?? series[0]?.unit
  const resolution = series[0]?.resolution
  // Every series in one chart is the same metric, so the note is shared.
  const description = series.find((s) => s.description)?.description

  // Merge the per-series point lists onto a shared time axis. Recharts wants
  // one row per timestamp with a column per series.
  const { rows, names, allNames, foldedCount } = useMemo(() => {
    const byTime = new Map<string, Row>()
    const seriesNames: string[] = []
    const magnitude = new Map<string, number>()

    // Naming by label alone is only safe when the chart shows ONE metric. With
    // two keys sharing a label — busy vs current threads on the same connector
    // — both series would be called "http-nio-8080", collapse onto one column
    // and silently overwrite each other, leaving one line where there are two.
    const distinctKeys = new Set(series.map((s) => s.key)).size

    for (const s of series) {
      const metricName = s.displayName || s.key
      const hasLabel = s.label && s.label.length > 0
      const name = distinctKeys > 1
        ? (hasLabel ? `${metricName} · ${s.label}` : metricName)
        : (hasLabel ? s.label : metricName)
      seriesNames.push(name)
      let peak = 0
      for (const p of s.points) {
        let row = byTime.get(p.t)
        if (!row) {
          row = { t: p.t }
          byTime.set(p.t, row)
        }
        const v = p.avg ?? p.last ?? null
        row[name] = v
        if (v !== null && v > peak) peak = v
      }
      magnitude.set(name, peak)
    }

    const sorted = [...byTime.values()].sort(
      (a, b) => new Date(a.t).getTime() - new Date(b.t).getTime(),
    )

    // More series than there are distinguishable colours. Rather than let the
    // overflow share one grey — which would have hidden the single largest TM
    // client version among five other greys — keep the biggest contributors
    // and sum the rest into one honest, explicitly labelled "Other" band.
    if (!foldSeries || seriesNames.length <= SERIES_SLOTS) {
      // Sorted by magnitude when nothing is folded, so the legend leads with the
      // series that matter rather than with whatever order the API returned.
      const names = foldSeries
        ? seriesNames
        : [...seriesNames].sort((a, b) => (magnitude.get(b) ?? 0) - (magnitude.get(a) ?? 0))
      return { rows: sorted, names, allNames: names, foldedCount: 0 }
    }

    const ranked = [...seriesNames].sort(
      (a, b) => (magnitude.get(b) ?? 0) - (magnitude.get(a) ?? 0))
    const kept = ranked.slice(0, SERIES_SLOTS - 1)
    const folded = ranked.slice(SERIES_SLOTS - 1)
    const otherName = `Other (${folded.length})`

    // The folded columns stay on the row — the chart simply does not declare a
    // dataKey for them, while the table still shows every original series.
    // Deleting them here would make "switch to the table for every value" a lie.
    for (const row of sorted) {
      let total: number | null = null
      for (const name of folded) {
        const v = row[name]
        if (typeof v === 'number') total = (total ?? 0) + v
      }
      row[otherName] = total
    }

    return {
      rows: sorted,
      names: [...kept, otherName],
      allNames: ranked,
      foldedCount: folded.length,
    }
  }, [series, foldSeries])

  const latest = rows.length > 0 ? rows[rows.length - 1] : undefined
  const tickFormatter = timeTickFormatter(range, rows.map((r) => r.t))
  const stacked = mode === 'stacked-area'

  // With folding on, a chart never exceeds the eight slots, so these behave
  // exactly as before. With it off the hues cycle and the dash marks the lap.
  const colorAt = (i: number) => (foldSeries ? seriesColor(i) : seriesCycleColor(i))
  const dashAt = (i: number) => (foldSeries ? undefined : seriesDash(i))

  if (series.length === 0 || rows.length === 0) {
    return (
      <Card title={title} prefKey={prefKey} note={description}>
        <div className="banner empty">No data recorded for this range yet.</div>
      </Card>
    )
  }

  const ChartImpl = stacked || mode === 'area' ? AreaChart : LineChart

  return (
    <Card
      title={title}
      prefKey={prefKey}
      note={description}
      meta={
        <>
          {RESOLUTION_LABEL[resolution] ?? resolution}
          {' · '}
          <button type="button" className="link-button"
                  onClick={() => setShowTable((v) => !v)}>
            {showTable ? 'chart' : 'table'}
          </button>
        </>
      }
    >
      <>
        {showTable ? (
          <SeriesTable rows={rows} names={allNames} unit={effectiveUnit} />
        ) : (
          <ResponsiveContainer width="100%" height={height}>
            <ChartImpl data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="var(--grid-line)" strokeDasharray="0" vertical={false} />
              <XAxis
                dataKey="t"
                tickFormatter={tickFormatter}
                stroke="var(--axis-line)"
                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                tickLine={false}
                minTickGap={48}
              />
              <YAxis
                stroke="var(--axis-line)"
                tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={56}
                tickFormatter={(v: number) => formatValue(v, effectiveUnit)}
              />
              <Tooltip
                content={<ChartTooltip unit={effectiveUnit} />}
                cursor={{ stroke: 'var(--text-muted)', strokeWidth: 1 }}
              />
              {names.map((name, i) =>
                stacked || mode === 'area' ? (
                  <Area
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stackId={stacked ? 'stack' : undefined}
                    stroke={colorAt(i)}
                    strokeWidth={2}
                    strokeDasharray={dashAt(i)}
                    fill={colorAt(i)}
                    fillOpacity={stacked ? 0.85 : 0.18}
                    // A 2px surface-coloured seam keeps adjacent stacked bands
                    // legible where two similar hues meet.
                    strokeLinejoin="round"
                    isAnimationActive={false}
                    connectNulls={false}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface-1)' }}
                  />
                ) : (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={colorAt(i)}
                    strokeWidth={2}
                    strokeDasharray={dashAt(i)}
                    isAnimationActive={false}
                    connectNulls={false}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--surface-1)' }}
                  />
                ),
              )}
            </ChartImpl>
          </ResponsiveContainer>
        )}

        {/* A single series is named by the title, so it needs no legend box. */}
        {foldedCount > 0 && (
          <div className="card-meta" style={{ paddingTop: 'var(--space-2)' }}>
            {foldedCount} smaller series combined into “Other” — switch to the table for every value.
          </div>
        )}

        {names.length > 1 && (
          <div className="chart-legend">
            {names.map((name, i) => (
              <span className="item" key={name}>
                {/* A solid block cannot show a dash pattern, so where hues
                    repeat the swatch becomes a line drawn exactly as the chart
                    draws it. Otherwise two providers a lap apart would share
                    one legend swatch. */}
                {dashAt(i) === undefined || dashAt(i) === '0' ? (
                  <span className="swatch" style={{ background: colorAt(i) }} aria-hidden="true" />
                ) : (
                  <svg className="swatch-line" width="14" height="10" aria-hidden="true">
                    <line x1="0" y1="5" x2="14" y2="5" stroke={colorAt(i)}
                          strokeWidth="2.5" strokeDasharray={dashAt(i)} />
                  </svg>
                )}
                <span>{name}</span>
                <span className="value">
                  {formatValue(latest?.[name] as number | undefined, effectiveUnit)}
                </span>
              </span>
            ))}
          </div>
        )}
      </>
    </Card>
  )
}

function ChartTooltip({ active, payload, label, unit }: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
  unit?: string
}) {
  if (!active || !payload || payload.length === 0) return null

  // Biggest contributor first: on a stack of a dozen relations, alphabetical
  // order buries the one that actually moved.
  const sorted = [...payload].sort((a, b) => (b.value ?? 0) - (a.value ?? 0))

  return (
    <div className="chart-tooltip">
      <div className="when">{label ? new Date(label).toLocaleString() : ''}</div>
      {sorted.slice(0, 12).map((entry) => (
        <div className="row" key={entry.name}>
          <span className="name">
            <span
              className="swatch"
              style={{ background: entry.color, width: 8, height: 8, borderRadius: 2 }}
              aria-hidden="true"
            />
            {entry.name}
          </span>
          <span className="num">{formatValue(entry.value, unit)}</span>
        </div>
      ))}
      {sorted.length > 12 && (
        <div className="row muted">
          <span>+{sorted.length - 12} more</span>
        </div>
      )}
    </div>
  )
}

/** The table view. Required relief for the light-mode contrast warning. */
function SeriesTable({ rows, names, unit }: { rows: Row[]; names: string[]; unit?: string }) {
  // Newest first, capped: this is for reading values, not for scrolling a month.
  const recent = [...rows].reverse().slice(0, 200)
  return (
    <div style={{ maxHeight: 260, overflow: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th>Time</th>
            {names.map((n) => <th key={n} className="num">{n}</th>)}
          </tr>
        </thead>
        <tbody>
          {recent.map((row) => (
            <tr key={row.t}>
              <td className="mono">{new Date(row.t).toLocaleString()}</td>
              {names.map((n) => (
                <td key={n} className="num">
                  {formatValue(row[n] as number | undefined, unit)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
